import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import CustomersPage from './pages/CustomersPage';
import TicketsPage from './pages/TicketsPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import PublicElement from './publicPages/PublicElement';
import PublicElementRedirect from './publicPages/PublicElementRedirect';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { Provider } from 'react-redux';
import store from './redux/store';
import CustomerDetailsPage from './pages/CustomerDetailsPage';
import Projects from './pages/Projects';
import TasksPage from './pages/TasksPage';
import DashboardPage from './pages/DashboardPage';
import ErrorBoundary from './components/ErrorBoundary';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import ProfilePage from './pages/ProfilePage';
import PublicBlueprintPage from './pages/PublicBlueprintPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';

function PrivateRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? <Navigate to="/customers" replace /> : children;
}

function AppContent() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      <Route
        path="/doc/:businessId/:moduleName/:structureId/:elementId"
        element={
          <PublicRoute>
            <PublicElement />
          </PublicRoute>
        }
      />
      <Route
        path="/redirect/:businessId/:moduleName/:structureId/:elementId"
        element={
          <PublicRoute>
            <PublicElementRedirect />
          </PublicRoute>
        }
      />

      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:customerId" element={<CustomerDetailsPage />} />
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="knowledge-base" element={<KnowledgeBasePage />} />
        <Route path="projects" element={<Projects />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      <Route path="/blueprint/:blueprintId" element={<PublicBlueprintPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
              <AuthProvider>
                <AppContent />
              </AuthProvider>
            </Router>
          </ThemeProvider>
        </I18nextProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
